import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { SessionContext, useUi, useOptions, useSession } from "./state/context.js";
import { PortalContext } from "./ui/floating.js";
import type { EditorSession } from "./state/session.js";
import { createActions } from "./state/actions.js";
import { Toolbar } from "./toolbar/Toolbar.js";
import { LeftPanel } from "./panels/LeftPanel.js";
import { AiProposal } from "./panels/AiProposal.js";
import { Canvas } from "./canvas/Canvas.js";
import { Inspector } from "./inspector/Inspector.js";
import { PanelResizer } from "./ui/PanelResizer.js";
import { Boundary } from "./ui/Boundary.js";
import { useElementWidth } from "./ui/use-element-size.js";

/** Width below which the panels become drawers over the canvas. See `isNarrow`. */
const NARROW_BELOW = 820;

/** The React root of the designer. Hosts should use `createReportDesigner` or `@broadpaper/react`. */
export function ReportDesignerRoot({ session }: { session: EditorSession }) {
  const actions = useMemo(() => createActions(session), [session]);
  return (
    <SessionContext.Provider value={session}>
      <Shell actions={actions} />
    </SessionContext.Provider>
  );
}

function Shell({ actions }: { actions: ReturnType<typeof createActions> }) {
  const options = useOptions();
  const [portal, setPortal] = useState<HTMLElement | null>(null);
  const session = useSession();
  const leftOpen = useUi((u) => u.leftOpen);
  const rightOpen = useUi((u) => u.rightOpen);
  const leftWidth = useUi((u) => u.leftWidth);
  const rightWidth = useUi((u) => u.rightWidth);
  const narrow = useUi((u) => u.narrow);
  const rootRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(rootRef);

  /**
   * Below this there is not room for a page between two panels: the defaults
   * alone are 272 + 296, and an A4 page at 100 % is another 794. Anything
   * narrower was being given a canvas of a few dozen pixels, which is how the
   * designer came to be unusable on a phone rather than merely cramped.
   *
   * `width === 0` is "not measured yet", not "very narrow" — collapsing on the
   * first frame and expanding on the second reads as a glitch.
   */
  const isNarrow = width > 0 && width < NARROW_BELOW;

  useEffect(() => {
    if (isNarrow === narrow) return;
    // Entering narrow closes both panels, because a drawer covering the canvas
    // is not what someone wants to arrive at; leaving it restores neither, on
    // the grounds that reopening a panel is one click and having one appear
    // unbidden is a surprise.
    session.ui.patch(isNarrow ? { narrow: true, leftOpen: false, rightOpen: false } : { narrow: false });
  }, [isNarrow, narrow, session]);
  const appearance = options.appearance === "system" ? (typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : options.appearance ?? "light";

  // Global shortcuts that should work even when focus is in a panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const target = e.target as HTMLElement;
      const root = target.closest(".bpe");
      if (!root) return;
      if (target.closest("input, textarea, select, [contenteditable=true], .ProseMirror")) return;
      if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) actions.redo();
        else actions.undo();
      } else if (mod && e.key.toLowerCase() === "y") {
        e.preventDefault();
        actions.redo();
      } else if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void actions.save();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [actions]);

  const cls = [
    "bpe",
    !leftOpen && "bpe--no-left",
    !rightOpen && "bpe--no-right",
    narrow && "bpe--narrow",
    options.className
  ]
    .filter(Boolean)
    .join(" ");
  // A drawer is only over the canvas when there is no room beside it; the rest
  // of the time the panels are columns and there is nothing to dismiss.
  const scrim = narrow && (leftOpen || rightOpen);
  return (
    <PortalContext.Provider value={portal}>
      <div ref={rootRef} className={cls} data-appearance={appearance} style={{ "--bpe-panel-w": `${leftWidth}px`, "--bpe-inspector-w": `${rightWidth}px` } as CSSProperties}>
        <Boundary area="The toolbar" slot="toolbar"><Toolbar actions={actions} /></Boundary>
        {leftOpen ? (
          <>
            <Boundary area="The insert panel" slot="left"><LeftPanel actions={actions} /></Boundary>
            {/* Dragging an edge is meaningless for a drawer that is already as
                wide as the room allows, and the handle would sit under a thumb. */}
            {!narrow ? (
              <PanelResizer side="left" width={leftWidth} min={200} max={420} label="Resize the insert panel" onChange={(leftWidth) => session.ui.patch({ leftWidth })} />
            ) : null}
          </>
        ) : (
          <div />
        )}
        <Boundary area="The canvas" slot="canvas"><Canvas actions={actions} /></Boundary>
        {/* Over the canvas rather than in it: the proposal is drawn from its own
            template, and the canvas keeps showing — and editing — the real one. */}
        {options.ai ? <Boundary area="The AI proposal" slot="canvas"><AiProposal /></Boundary> : null}
        {rightOpen ? (
          <>
            {!narrow ? (
              <PanelResizer side="right" width={rightWidth} min={260} max={560} label="Resize the properties panel" onChange={(rightWidth) => session.ui.patch({ rightWidth })} />
            ) : null}
            <Boundary area="The properties panel" slot="right"><Inspector actions={actions} /></Boundary>
          </>
        ) : (
          <div />
        )}
        {scrim ? (
          <button
            type="button"
            className="bpe-scrim"
            aria-label="Close the panel"
            onClick={() => session.ui.patch({ leftOpen: false, rightOpen: false })}
          />
        ) : null}
        <div className="bpe-portal bpe-scope" data-appearance={appearance} ref={setPortal} />
      </div>
    </PortalContext.Provider>
  );
}
